import { getFormErrorMessage } from "@core/utility/get-error-message";
import { PatientFormRule, PatientFormSchema } from "@factory/form/form.rule";
import { useFormDefaultPatient } from "@factory/form/form-default-patient.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponsePatientModel } from "@factory/model/query-model-patient";
import { useQyGetContract } from "@factory/query/contract.query";
import { useQyGetPatientSummary } from "@factory/query/dashboard.query";
import { useQyGetGoogleCloudAssetPreview } from "@factory/query/google-cloud-storage.query";
import {
  useQyDeletePatient,
  useQyGetPatientById,
  useQyUpdatePatient,
} from "@factory/query/patient.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { confirmDialog } from "primereact/confirmdialog";
import { useMemo } from "react";
import { FieldErrors, useForm } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";

export function useEditPatient() {
  // LOCAL DECLARATION
  const { formatDateForInput } = useDateTimeContext();
  const { getDefault } = useFormDefaultPatient();
  const navigate = useNavigate();
  const { mapUpdatePatient, mapDeletePatient } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { patientId } = useParams();
  const location = useLocation();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];
  const menuItems = [
    {
      label: "Information",
      icon: "pi pi-file-edit",
      command: () => navigate(""),
    },
    {
      label: "Visits",
      icon: "pi pi-list-check",
      command: () => navigate(`visit`),
    },
    {
      label: "Contracts",
      icon: "pi pi-file",
      command: () => navigate(`contract`),
    },
    {
      label: "History",
      icon: "pi pi-history",
      command: () => navigate(`history`),
    },
  ];

  // QUERY DECLARATION
  // API GETBYID Patient
  const handleGetApiSuccess = (data: GetResponsePatientModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

    if (responseData?.avatar) {
      viewAsset(responseData?.avatar, {
        onSuccess(data) {
          const assetDisplay = URL.createObjectURL(data);
          setValue("photo", assetDisplay);
        },
      });
    }

    setValue("firstName", responseData?.first_name || "");
    setValue("middleName", responseData?.middle_name || "");
    setValue("lastName", responseData?.last_name || "");
    if (responseData?.birthday) {
      setValue("birthday", formatDateForInput(responseData?.birthday));
    }
    setValue("gender", responseData?.gender || "");
    setValue("address", responseData?.address || "");
    setValue("email", responseData?.email || "");
    setValue("mobileNumber", responseData?.mobile_number || "");
    setValue("isTakingMedication", Boolean(responseData?.is_taking_medication));
    setValue("medicationDetails", responseData?.medication_details || "");
    setValue("hasAllergies", Boolean(responseData?.has_allergies));
    setValue("allergyDetails", responseData?.allergy_details || "");
    setValue(
      "hasMedicalConditions",
      Boolean(responseData?.has_medical_conditions),
    );
    setValue(
      "medicalConditionDetails",
      responseData?.medical_conditon_details || "",
    );
    setValue("wasRecentlyUnwell", Boolean(responseData?.was_recently_unwell));
    setValue("unwellDetails", responseData?.unwell_details || "");
    setValue(
      "otherMedicalConditions",
      responseData?.other_medical_conditions || "",
    );
  };
  const {
    data: patientResponse,
    isLoading,
    isError: patientError,
  } = useQyGetPatientById(patientId || "", handleGetApiSuccess);
  const patientData = patientResponse?.data?.[0];

  // API VIEW ASSET
  const { mutate: viewAsset } = useQyGetGoogleCloudAssetPreview();

  // API UPDATE Patient
  const handleUpdateApiSuccess = () => {
    showSuccess("Patient updated successfully");
  };
  const { mutate: updatePatient, isLoading: isEditLoading } =
    useQyUpdatePatient(handleUpdateApiSuccess);

  // API DELETE Patient
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("Patient deleted successfully");
  };
  const { mutate: deletePatient } = useQyDeletePatient(handleDeleteApiSuccess);

  // LOCAL FUNCTIONS
  const formMethod = useForm<PatientFormSchema>({
    defaultValues: getDefault(patientData),
    resolver: zodResolver(PatientFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: PatientFormSchema) => {
    if (!patientData || !patientData.code) {
      throw new Error("No patient data");
    }

    const formData = mapUpdatePatient(form, patientData.code);
    updatePatient(formData);
  };
  const handleValidateError = (err: FieldErrors<PatientFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!patientData || !patientData.code) {
      throw new Error("No patient data found");
    }

    const form = mapDeletePatient(patientData.code);
    deletePatient(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;

      case ActionEnum.Delete:
        confirmDialog({
          message:
            "Are you sure you want to delete this record?\nAll the contracts and waiver under the account will be deleted as well.",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

  const pathSegments = location.pathname.split("/").filter(Boolean);
  const isExactPatientRoute =
    pathSegments.length === 3 && pathSegments[1] === "patient";

  // API GET CONTRACTS
  const { data: contractResponse, isLoading: isLoadingContracts } =
    useQyGetContract(
      "",
      9999,
      0,
      undefined,
      {
        patient: patientId || "",
      },
      !!patientId,
      undefined,
    );
  const contractData = contractResponse?.data || [];

  const totalAmount = useMemo(() => {
    return contractData.reduce(
      (sum, contract) => sum + (contract.amount || 0),
      0,
    );
  }, [contractData]);

  const totalBalance = useMemo(() => {
    return contractData.reduce(
      (sum, contract) => sum + (contract.total_balance || 0),
      0,
    );
  }, [contractData]);

  const totalContracts = useMemo(() => contractData.length, [contractData]);

  const { data: patientSummary, isLoading: isLoadingPatientSummary } =
    useQyGetPatientSummary(
      patientId || "",
      undefined,
      undefined,
      undefined,
      !!patientId,
    );

  return {
    patientId,
    patientData,
    isLoading,
    patientError,
    formMethod,
    actions,
    isEditLoading,
    menuItems,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
    isExactPatientRoute,
    contractData,
    isLoadingContracts,
    totalAmount,
    totalBalance,
    totalContracts,
    patientSummary,
    isLoadingPatientSummary,
  };
}
